// New tests use published Chester oxygen costs and HR-on-workload OLS.
// Keep older values available for immutable signed records and save recovery.
export const LEGACY_CLINIC_PROTOCOL_VERSION = 'stepiq-30cm-cr10-v1';
export const FROZEN_V2_CLINIC_PROTOCOL_VERSION = 'stepiq-30cm-cr10-v2';
export const ORSTED_CLINIC_PROTOCOL_VERSION = 'stepiq-orsted-30cm-v3';
export const CHESTER_CLINIC_PROTOCOL_VERSION = 'stepiq-chester-30cm-v4';
export const CLINIC_PROTOCOL_VERSION = CHESTER_CLINIC_PROTOCOL_VERSION;
export const FROZEN_V2_CLINIC_30CM_STAGE_COSTS = [16, 21, 27, 32, 37] as const;
export const ORSTED_CLINIC_30CM_STAGE_COSTS = [14, 20, 26, 32, 36] as const;
export const CLINIC_30CM_STAGE_COSTS = [16, 21, 27, 32, 37] as const;
export const LEGACY_CLINIC_30CM_STAGE_COSTS = [17.3, 21.9, 26.5, 31.1, 35.6] as const;

export type ClinicProtocolVersion = typeof LEGACY_CLINIC_PROTOCOL_VERSION | typeof FROZEN_V2_CLINIC_PROTOCOL_VERSION | typeof ORSTED_CLINIC_PROTOCOL_VERSION | typeof CHESTER_CLINIC_PROTOCOL_VERSION;
export type ClinicRpeScale = 'CR10 (0-10)' | 'Ørsted Appendix A (0-10)';
export interface ClinicProtocolReading { hr: number; vo2Estimate: number }
export interface ClinicRegression {
  slope: number;
  intercept: number;
  direction: 'hr-on-workload' | 'workload-on-hr';
}

function protocolSettings(version: ClinicProtocolVersion) {
  switch (version) {
    case LEGACY_CLINIC_PROTOCOL_VERSION:
    case FROZEN_V2_CLINIC_PROTOCOL_VERSION:
      return { minimumStages: 2, rpeScale: 'CR10 (0-10)' as const, direction: 'workload-on-hr' as const };
    case ORSTED_CLINIC_PROTOCOL_VERSION:
    case CHESTER_CLINIC_PROTOCOL_VERSION:
      return { minimumStages: 3, rpeScale: 'Ørsted Appendix A (0-10)' as const, direction: 'hr-on-workload' as const };
    default: throw new Error('Unsupported clinic protocol version.');
  }
}

export function stageCostsForProtocol(version: ClinicProtocolVersion) {
  switch (version) {
    case LEGACY_CLINIC_PROTOCOL_VERSION: return LEGACY_CLINIC_30CM_STAGE_COSTS;
    case FROZEN_V2_CLINIC_PROTOCOL_VERSION: return FROZEN_V2_CLINIC_30CM_STAGE_COSTS;
    case ORSTED_CLINIC_PROTOCOL_VERSION: return ORSTED_CLINIC_30CM_STAGE_COSTS;
    case CHESTER_CLINIC_PROTOCOL_VERSION: return CLINIC_30CM_STAGE_COSTS;
    default: throw new Error('Unsupported clinic protocol version.');
  }
}

export function minimumStagesForProtocol(version: ClinicProtocolVersion): number {
  return protocolSettings(version).minimumStages;
}

export function rpeScaleForProtocol(version: ClinicProtocolVersion): ClinicRpeScale {
  return protocolSettings(version).rpeScale;
}

export function maxHRForProtocol(version: ClinicProtocolVersion, age: number, betaBlocker: boolean): number {
  protocolSettings(version);
  if (version === ORSTED_CLINIC_PROTOCOL_VERSION || version === CHESTER_CLINIC_PROTOCOL_VERSION) return 220 - age;
  return betaBlocker ? Math.round(164 - 0.7 * age) : 220 - age;
}

export function referenceHRForProtocol(version: ClinicProtocolVersion, maxHR: number): number {
  protocolSettings(version);
  if (version === ORSTED_CLINIC_PROTOCOL_VERSION || version === CHESTER_CLINIC_PROTOCOL_VERSION) return maxHR * 0.8;
  return Math.round(maxHR * 0.8);
}

export function clinicRegressionForProtocol(version: ClinicProtocolVersion, levels: readonly ClinicProtocolReading[]): ClinicRegression | null {
  const settings = protocolSettings(version);
  if (levels.length < settings.minimumStages || levels.some(level => !Number.isFinite(level.hr) || !Number.isFinite(level.vo2Estimate))) return null;
  if (settings.direction === 'hr-on-workload') {
    const n = levels.length;
    const sx = levels.reduce((sum, level) => sum + level.vo2Estimate, 0);
    const sy = levels.reduce((sum, level) => sum + level.hr, 0);
    const denominator = n * levels.reduce((sum, level) => sum + level.vo2Estimate * level.vo2Estimate, 0) - sx * sx;
    if (denominator <= 0) return null;
    const slope = (n * levels.reduce((sum, level) => sum + level.vo2Estimate * level.hr, 0) - sx * sy) / denominator;
    return { slope, intercept: (sy - slope * sx) / n, direction: settings.direction };
  }
  // Preserve the v1/v2 accumulation order used by the signed-save validator.
  const n = levels.length;
  const sx = levels.reduce((sum, level) => sum + level.hr, 0);
  const sy = levels.reduce((sum, level) => sum + level.vo2Estimate, 0);
  const denominator = n * levels.reduce((sum, level) => sum + level.hr * level.hr, 0) - sx * sx;
  if (denominator <= 0) return null;
  const slope = (n * levels.reduce((sum, level) => sum + level.hr * level.vo2Estimate, 0) - sx * sy) / denominator;
  return { slope, intercept: (sy - slope * sx) / n, direction: settings.direction };
}

export function clinicScoreForProtocol(version: ClinicProtocolVersion, levels: readonly ClinicProtocolReading[], maxHR: number): number {
  const fit = clinicRegressionForProtocol(version, levels);
  if (!fit || fit.slope <= 0 || !Number.isFinite(maxHR)) return NaN;
  const rawScore = fit.direction === 'hr-on-workload' ? (maxHR - fit.intercept) / fit.slope : Math.max(8, fit.slope * maxHR + fit.intercept);
  return Number.isFinite(rawScore) && rawScore > 0 ? rawScore : NaN;
}
